<template>
    <div class="flex justify-center items-center h-screen">
        <!-- Loading state -->
        <div v-if="loading" class="text-center">
            <div
                class="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-primary mx-auto mb-4"
            ></div>
            <strong class="text-theme-base text-lg">Cargando...</strong>
        </div>

        <div
            v-else-if="errorType === 'USER_NOT_INVITED'"
            class="max-w-md w-full mx-4"
        >
            <div class="bg-theme-card rounded-lg shadow-lg p-8 text-center">
                <div class="mb-6">
                    <svg
                        class="w-20 h-20 text-theme-danger mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                </div>
                <h2 class="text-2xl font-bold text-theme-base mb-4">
                    Acceso No Autorizado
                </h2>
                <p class="text-theme-muted mb-6">
                    No tienes permiso para acceder a esta plataforma. Por favor,
                    contacta al administrador para solicitar acceso.
                </p>
                <button
                    class="w-full bg-stack-primary hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                    @click="goToLogin"
                >
                    Volver al Login
                </button>
            </div>
        </div>

        <div
            v-else-if="errorType === 'GENERIC_ERROR'"
            class="max-w-md w-full mx-4"
        >
            <div class="bg-theme-card rounded-lg shadow-lg p-8 text-center">
                <div class="mb-6">
                    <svg
                        class="w-20 h-20 text-theme-warning mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
                <h2 class="text-2xl font-bold text-theme-base mb-4">
                    Error de Autenticación
                </h2>
                <p class="text-theme-muted mb-2">
                    Ocurrió un error durante el proceso de inicio de sesión:
                </p>
                <div
                    class="bg-theme-danger bg-opacity-10 border border-theme-danger rounded-lg p-4 mb-6"
                >
                    <p class="text-sm text-white font-mono break-words">
                        {{ errorMessage }}
                    </p>
                </div>
                <button
                    class="w-full bg-stack-primary hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
                    @click="goToLogin"
                >
                    Intentar Nuevamente
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

import { LANDING_SOURCE_STORAGE_KEY } from "@/common/constants";
import { isSafeRedirectPath } from "@/common/lib/redirect";

export default {
    name: "CallbackPage",
    data() {
        return {
            loading: true,
            errorType: null, // 'USER_NOT_INVITED' | 'GENERIC_ERROR' | null
            errorMessage: ""
        };
    },
    computed: {
        ...mapState("auth", ["user"]),
        ...mapState("commons", ["postLoginRedirect"])
    },
    async mounted() {
        this.validateAuth();
    },
    methods: {
        ...mapActions("auth", ["getAndApplyCurrentSession"]),
        ...mapActions("commons", ["listOwnMerchants"]),
        ...mapMutations("commons", ["CLEAR_POST_LOGIN_REDIRECT"]),

        async validateAuth() {
            try {
                const {
                    error: errorParam,
                    error_description: errorDescription
                } = this.$route.query;

                if (errorParam || errorDescription) {
                    this.handleAuthError(errorDescription || errorParam);
                    return;
                }

                const { needOnboardingTenant, needOnboardingUser, tenantId } =
                    await this.getAndApplyCurrentSession();
                const redirectTo = this.postLoginRedirect;
                this.CLEAR_POST_LOGIN_REDIRECT();

                if (this.user?.sub) {
                    this.$analytics.identifyUser(this.user.sub, {
                        $email: this.user.email,
                        $name: [this.user.name, this.user.lastName]
                            .filter(Boolean)
                            .join(" ")
                    });
                }
                const source = sessionStorage.getItem(
                    LANDING_SOURCE_STORAGE_KEY
                );
                this.$analytics.trackEvent("user_logged_in_google", {
                    method: "google",
                    need_onboarding_user: !!needOnboardingUser,
                    need_onboarding_tenant: !!needOnboardingTenant,
                    ...(source ? { source } : {})
                });
                sessionStorage.removeItem(LANDING_SOURCE_STORAGE_KEY);

                if (needOnboardingTenant) {
                    this.pushRoute("/onboarding/multitenant");
                    return;
                }
                if (needOnboardingUser) {
                    this.pushRoute("/onboarding/marketplace");
                    return;
                }

                if (
                    tenantId &&
                    this.$ability.hasPermission("merchant:ListOwn")
                ) {
                    await this.listOwnMerchants({
                        required: true
                    });
                }

                this.pushRoute(redirectTo || "/app");
            } catch (error) {
                console.error(error);
                this.errorType = "GENERIC_ERROR";
                this.errorMessage = error.message || "Error desconocido";
                this.loading = false;
            }
        },

        handleAuthError(errorText) {
            this.loading = false;
            console.error("Auth error:", errorText);

            try {
                const jsonMatch = errorText.match(/\{.*\}/);

                if (jsonMatch) {
                    const errorData = JSON.parse(jsonMatch[0]);

                    if (
                        errorData.errorMessage === "USER_NOT_INVITED" ||
                        errorData.action === "DENY"
                    ) {
                        this.errorType = "USER_NOT_INVITED";
                        return;
                    }

                    this.errorType = "GENERIC_ERROR";
                    this.errorMessage = errorData.errorMessage || errorText;
                } else {
                    if (errorText.includes("USER_NOT_INVITED")) {
                        this.errorType = "USER_NOT_INVITED";
                    } else {
                        this.errorType = "GENERIC_ERROR";
                        this.errorMessage = errorText;
                    }
                }
            } catch (e) {
                this.errorType = "GENERIC_ERROR";
                this.errorMessage = errorText;
            }
        },

        goToLogin() {
            this.$router.push("/login");
        },

        pushRoute(route) {
            // Un postLoginRedirect viejo puede seguir apuntando al propio
            // /callback: quedo persistido en localStorage antes de arreglar el
            // filtro, y navegar ahi reusa este mismo componente (misma ruta,
            // solo cambia el query) sin volver a correr mounted().
            const target = isSafeRedirectPath(route) ? route : "/app";
            setTimeout(() => {
                this.$router.push(target).catch(() => {
                    this.errorType = "GENERIC_ERROR";
                    this.errorMessage =
                        "No pudimos abrir tu cuenta. Intenta de nuevo.";
                    this.loading = false;
                });
            }, 100);
        }
    }
};
</script>
